import {
  getFileNameFromPath,
  isValidImage,
  isValidVideo,
  MaterialIcon,
  useLoading,
  VideoThumbnail,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LoadingSpinner } from 'baseui/button/styled-components';
import { LabelSmall } from 'baseui/typography';
import { memo, useEffect, useState } from 'react';
import { MEDIA_PLATFORM_ORIGIN, MEDIA_POSITION_ORIGIN, MEDIA_SAFE_GPT_ORIGIN } from '../../constants/media';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { getS3FileUrlByKey } from '../../services/appService';
import { getDownloadMediaUrlByKey } from '../../services/evidenceService';
import { MediaOrigin } from '../../types/media';
import { NotificationOrigin } from '../../types/notificationConfig';
import { downloadBlob } from '../../utils/files';

type Props = {
  mediaKey?: string;
  origin: MediaOrigin | NotificationOrigin;
  onPreview?: (url: string) => void;
  onlyPreview?: boolean;
  minHeight?: number;
  cover?: boolean;
};

function MediaThumbnail({
  mediaKey,
  origin,
  onPreview,
  onlyPreview = false,
  minHeight = 128,
  cover = false,
}: Readonly<Props>) {
  const { css, theme } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const { loading: downloading, startLoading: startDownloading, stopLoading: stopDownloading } = useLoading();
  const { showFailFeedback } = useFeedback();
  const [mediaUrl, setMediaUrl] = useState('');

  const mediaContainerStyles = css({
    width: '100%',
    position: 'relative',
    minHeight: minHeight + 'px',
    background: theme.colors.backgroundInversePrimary,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  });
  const mediaStyles = css({
    maxWidth: '100%',
    height: '100%',
    objectFit: cover ? 'cover' : 'contain',
    minHeight: minHeight + 'px',
  });
  const downloadButtonStyles = css({
    position: 'absolute',
    top: theme.sizing.scale200,
    right: theme.sizing.scale200,
  });
  const fullScreenStyles = css({
    position: 'absolute',
    bottom: theme.sizing.scale200,
    right: theme.sizing.scale200,
  });

  useEffect(() => {
    const loadMedia = async () => {
      if (!mediaKey) {
        stopLoading();
        return;
      }
      startLoading();
      const fromExternal = [NotificationOrigin.safeGpt, MEDIA_SAFE_GPT_ORIGIN].includes(origin);
      const { url, error } = await getS3FileUrlByKey(mediaKey, fromExternal);
      if (!error && url) {
        setMediaUrl(url);
      }
      stopLoading();
    };

    loadMedia();
  }, [mediaKey]);

  const download = async (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!mediaKey) return;
    startDownloading();
    const isSafegptOrigin = [NotificationOrigin.safeGpt, MEDIA_SAFE_GPT_ORIGIN].includes(origin);
    const isPlatformOrigin = [NotificationOrigin.alarms, MEDIA_PLATFORM_ORIGIN].includes(origin);
    const defaultOrigin = isPlatformOrigin ? MEDIA_PLATFORM_ORIGIN : MEDIA_POSITION_ORIGIN;
    const mediaOrigin = isSafegptOrigin ? MEDIA_SAFE_GPT_ORIGIN : defaultOrigin;
    const { file, error } = await getDownloadMediaUrlByKey(mediaKey, mediaOrigin);
    if (error) {
      showFailFeedback(error);
    } else if (file) {
      downloadBlob(file, getFileNameFromPath(mediaKey));
    }
    stopDownloading();
  };

  return (
    <div className={mediaContainerStyles}>
      {loading && <LoadingSpinner />}
      {!loading && !mediaUrl && <LabelSmall color={theme.colors.contentInversePrimary}>No disponible</LabelSmall>}
      {!loading && !!mediaUrl && (
        <>
          {isValidVideo(mediaUrl) && (
            <VideoThumbnail
              url={mediaUrl}
              videoStyle={{
                width: '100%',
                height: '100%',
                objectFit: cover ? 'cover' : 'contain',
                cursor: onPreview ? 'pointer' : undefined,
                minHeight: minHeight + 'px',
              }}
              onClick={onPreview ? () => onPreview?.(mediaUrl) : undefined}
            />
          )}
          {isValidImage(mediaUrl) && (
            <img
              src={mediaUrl}
              alt=""
              className={mediaStyles}
            />
          )}
          {!onlyPreview && (
            <Button
              size="mini"
              kind="primary"
              shape="square"
              className={downloadButtonStyles}
              colors={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', color: theme.colors.contentInversePrimary }}
              onClick={download}
              isLoading={downloading}
            >
              <MaterialIcon
                name="download"
                size="mini"
                color={theme.colors.contentInversePrimary}
              />
            </Button>
          )}
          {onPreview && (
            <Button
              size="mini"
              kind="tertiary"
              shape="square"
              className={fullScreenStyles}
              colors={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', color: theme.colors.contentInversePrimary }}
              onClick={() => {
                onPreview(mediaUrl);
              }}
            >
              <MaterialIcon
                name="aspect_ratio"
                color={theme.colors.contentInversePrimary}
              />
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export default memo(MediaThumbnail);
